"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  approximatePercentile,
  approximateRank,
  candidatesAbove,
  isPercentile,
  isPositiveCount,
  isRank,
  isValidCounts,
  percentileFromCounts,
} from "@/lib/tool-kit/exam-scores/jee";

/** English strings. A translated content file overrides these through `ui`. */
export const JEE_MAIN_PERCENTILE_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to work out?",
  modes: {
    counts: "NTA percentile from candidate counts",
    toRank: "Approximate rank from a percentile",
    toPercentile: "Approximate percentile from a rank",
  },
  appearedLabel: "Candidates who appeared",
  appearedHint:
    "For the percentile formula, use your own session. For a rank, use all candidates who appeared in the paper.",
  atOrBelowLabel: "Candidates with a raw score equal to or less than yours",
  percentileLabel: "Your NTA percentile (0–100)",
  rankLabel: "Your rank",
  invalidCounts:
    "Enter whole numbers, with the candidates at or below your score between 1 and the number who appeared.",
  invalidPercentile:
    "Enter a percentile from 0 to 100 and a whole number of candidates.",
  invalidRank:
    "Enter whole numbers, with a rank between 1 and the number of candidates.",
  enter: "Fill in both boxes to see a result.",
  results: "Result",
  percentile: "NTA percentile",
  workingCounts: "100 × {below} ÷ {appeared} = {percentile}",
  rank: "Approximate rank",
  above: "Candidates with a higher score",
  workingRank: "{above} candidates above + 1 = about {rank}",
  rankPercentile: "Approximate percentile",
  workingRankPercentile:
    "100 × ({appeared} − {rank} + 1) ÷ {appeared} = {percentile}",
  formulaHeading: "NTA's definition",
  formula:
    "Percentile = 100 × (candidates in the session with a raw score equal to or less than yours) ÷ (candidates who appeared in the session). The session's highest score gets 100.",
  approximationNote:
    "Rank figures are approximations. They ignore ties, which share a percentile, and the way percentiles from different sessions are merged into one merit list.",
  totalNote:
    "NTA computes the total percentile from total raw scores. It is not an average of the Physics, Chemistry and Mathematics percentiles.",
  copyResult: "Copy result",
  copied: "Copied!",
  summaryPercentile:
    "NTA percentile {percentile} ({below} of {appeared} candidates at or below)",
  summaryRank: "Percentile {percentile} of {appeared} candidates ≈ rank {rank}",
  summaryRankPercentile:
    "Rank {rank} of {appeared} candidates ≈ percentile {percentile}",
} as const;

type Strings = typeof JEE_MAIN_PERCENTILE_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;
type Mode = "counts" | "toRank" | "toPercentile";

const SEVEN = { minimumFractionDigits: 7, maximumFractionDigits: 7 };
const MODES: readonly Mode[] = ["counts", "toRank", "toPercentile"];

function read(raw: string): number | null {
  return raw.trim() === "" ? null : parseNumberInput(raw);
}

interface Computed {
  headline: string;
  headlineLabel: string;
  working: string;
  summary: string;
  extra?: { label: string; value: string };
}

function compute(
  mode: Mode,
  inputs: { appeared: number | null; second: number | null },
  strings: Strings,
  fmt: Format
): Computed | null {
  const { appeared, second } = inputs;
  if (mode === "counts" && isValidCounts(appeared, second)) {
    const value = percentileFromCounts(appeared as number, second as number);
    const shown = fmt(value, SEVEN);
    return {
      headline: shown,
      headlineLabel: strings.percentile,
      working: t(strings.workingCounts, {
        below: fmt(second as number),
        appeared: fmt(appeared as number),
        percentile: shown,
      }),
      summary: t(strings.summaryPercentile, {
        percentile: shown,
        below: fmt(second as number),
        appeared: fmt(appeared as number),
      }),
    };
  }
  if (mode === "toRank" && isPositiveCount(appeared) && isPercentile(second)) {
    const rank = approximateRank(second, appeared);
    const above = Math.round(candidatesAbove(second, appeared));
    return {
      headline: fmt(rank),
      headlineLabel: strings.rank,
      working: t(strings.workingRank, { above: fmt(above), rank: fmt(rank) }),
      summary: t(strings.summaryRank, {
        percentile: fmt(second, SEVEN),
        appeared: fmt(appeared),
        rank: fmt(rank),
      }),
      extra: { label: strings.above, value: fmt(above) },
    };
  }
  if (mode === "toPercentile" && isRank(second, appeared)) {
    const value = approximatePercentile(second as number, appeared as number);
    const shown = fmt(value, SEVEN);
    return {
      headline: shown,
      headlineLabel: strings.rankPercentile,
      working: t(strings.workingRankPercentile, {
        appeared: fmt(appeared as number),
        rank: fmt(second as number),
        percentile: shown,
      }),
      summary: t(strings.summaryRankPercentile, {
        rank: fmt(second as number),
        appeared: fmt(appeared as number),
        percentile: shown,
      }),
    };
  }
  return null;
}

export default function JeeMainPercentileCalculatorWidget() {
  const strings = useToolUi(JEE_MAIN_PERCENTILE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("counts");
  const [appearedRaw, setAppearedRaw] = useState("41326");
  const [secondRaw, setSecondRaw] = useState("37244");
  const appeared = read(appearedRaw);
  const second = read(secondRaw);
  const result = compute(mode, { appeared, second }, strings, fmt);
  const secondLabel = {
    counts: strings.atOrBelowLabel,
    toRank: strings.percentileLabel,
    toPercentile: strings.rankLabel,
  }[mode];
  const invalidMessage = {
    counts: strings.invalidCounts,
    toRank: strings.invalidPercentile,
    toPercentile: strings.invalidRank,
  }[mode];
  const filled = appearedRaw.trim() !== "" && secondRaw.trim() !== "";

  function changeMode(next: Mode) {
    setMode(next);
    setSecondRaw("");
  }

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="jee-mode"
        onChange={changeMode}
        options={MODES.map((id) => ({ value: id, label: strings.modes[id] }))}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.appearedHint}
          id="jee-appeared"
          label={strings.appearedLabel}
          numeric
          onChange={setAppearedRaw}
          value={appearedRaw}
        />
        <TextField
          id="jee-second"
          label={secondLabel}
          numeric
          onChange={setSecondRaw}
          value={secondRaw}
        />
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {result ? (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={result.headlineLabel}
                value={result.headline}
              />
              {result.extra ? (
                <StatCard
                  label={result.extra.label}
                  value={result.extra.value}
                />
              ) : null}
            </StatGrid>
            <p className="rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm">
              {result.working}
            </p>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyResult}
              small
              text={result.summary}
            />
          </>
        ) : null}
        {!result && filled ? <Notice>{invalidMessage}</Notice> : null}
        {filled ? null : <Notice>{strings.enter}</Notice>}
        {mode === "counts" ? null : (
          <p className="text-muted-foreground text-sm">
            {strings.approximationNote}
          </p>
        )}
        <div className="space-y-1 text-muted-foreground text-sm">
          <p>
            <strong className="text-foreground">
              {strings.formulaHeading}.
            </strong>{" "}
            {strings.formula}
          </p>
          <p>{strings.totalNote}</p>
        </div>
      </section>
    </div>
  );
}
